import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  query,
  orderBy,
  getDocs,
  serverTimestamp,
  Timestamp,
  DocumentData,
  QueryDocumentSnapshot,
} from 'firebase/firestore'
import { User } from 'firebase/auth'
import { db } from '@/lib/firebase'
import {
  Period,
  PeriodStatus,
  WizardStep,
  CreatePeriodData,
  UpdatePeriodData,
  UnlockHistoryEntry,
} from '@/types/period'
import { getMonthlyPeriodDates, generatePeriodName } from '@/utils/periodHelpers'

/**
 * Service for managing salary periods.
 * Handles period CRUD, locking/unlocking, and wizard state.
 *
 * Collection path: users/{userId}/periods/{periodId}
 * Document ID format: "yyyy-mm-01" (e.g., "2025-11-01")
 */
export class PeriodService {
  private static readonly COLLECTION_NAME = 'periods'

  /**
   * Get collection reference for a user
   */
  private static getUserCollection(user: User) {
    return collection(db, 'users', user.uid, this.COLLECTION_NAME)
  }

  /**
   * Get document reference for a specific period
   */
  private static getPeriodDocument(user: User, periodId: string) {
    return doc(db, 'users', user.uid, this.COLLECTION_NAME, periodId)
  }

  /**
   * Convert Firestore document to Period with proper typing
   */
  private static convertFirestoreDoc(
    doc: QueryDocumentSnapshot<DocumentData> | DocumentData,
    docId?: string
  ): Period {
    const data = 'data' in doc ? doc.data() : doc
    const id = docId || ('id' in doc ? doc.id : data.id)

    return {
      id,
      status: (data.status as PeriodStatus) || 'draft',
      startDate: data.startDate as Timestamp,
      endDate: data.endDate as Timestamp,
      payoutDate: data.payoutDate as Timestamp | undefined,
      name: data.name,
      wizardStep: data.wizardStep as WizardStep | undefined,
      lockedAt: data.lockedAt as Timestamp | undefined,
      lockedBy: data.lockedBy,
      unlockHistory: data.unlockHistory?.map((entry: DocumentData) => ({
        unlockedAt: entry.unlockedAt as Timestamp,
        unlockedBy: entry.unlockedBy,
        reason: entry.reason,
      })),
      excludedDriverIds: data.excludedDriverIds as string[] | undefined,
      createdAt: data.createdAt as Timestamp | undefined,
      updatedAt: data.updatedAt as Timestamp | undefined,
    }
  }

  /**
   * Validate period format (must be "yyyy-mm-01")
   */
  private static validatePeriodFormat(periodId: string): void {
    const periodRegex = /^\d{4}-\d{2}-01$/
    if (!periodRegex.test(periodId)) {
      throw new Error(
        'Ogiltigt periodformat. Måste vara "yyyy-mm-01" (t.ex. "2025-11-01")'
      )
    }
  }

  // ============================================================================
  // CRUD Operations
  // ============================================================================

  /**
   * Get a specific period by ID
   * Returns null if period doesn't exist
   */
  static async getPeriod(user: User, periodId: string): Promise<Period | null> {
    try {
      this.validatePeriodFormat(periodId)

      const docRef = this.getPeriodDocument(user, periodId)
      const docSnap = await getDoc(docRef)

      if (!docSnap.exists()) {
        return null
      }

      return this.convertFirestoreDoc(docSnap)
    } catch (error) {
      console.error('Error fetching period:', error)
      if (
        error instanceof Error &&
        error.message.includes('Ogiltigt periodformat')
      ) {
        throw error
      }
      throw new Error('Det gick inte att hämta perioden')
    }
  }

  /**
   * List all periods for a user, ordered by period descending (newest first)
   */
  static async listPeriods(user: User): Promise<Period[]> {
    try {
      const q = query(this.getUserCollection(user), orderBy('id', 'desc'))

      const querySnapshot = await getDocs(q)
      return querySnapshot.docs.map((doc) => this.convertFirestoreDoc(doc))
    } catch (error) {
      console.error('Error listing periods:', error)
      throw new Error('Det gick inte att hämta perioder')
    }
  }

  /**
   * Create a new period
   * Accepts either a month string (for standard monthly periods) or custom dates
   */
  static async createPeriod(
    user: User,
    data: CreatePeriodData
  ): Promise<Period> {
    try {
      let periodId: string
      let startDate: Date
      let endDate: Date
      let name: string | undefined = data.name

      // TODO: When implementing Create Period UI, add validation for partial dates
      // (only startDate OR only endDate provided). Currently falls through to month-based
      // logic which is safe but may be confusing. See Copilot review comment on PR #320.
      if (data.startDate && data.endDate) {
        // Custom date range
        startDate = data.startDate
        endDate = data.endDate
        // Generate period ID from start date (first of month)
        periodId =
          data.month ||
          `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-01`

        // Auto-generate name if not provided
        if (!name) {
          name = generatePeriodName(startDate, endDate)
        }
      } else if (data.month) {
        // Standard monthly period
        this.validatePeriodFormat(data.month)
        periodId = data.month
        const dates = getMonthlyPeriodDates(data.month)
        startDate = dates.startDate
        endDate = dates.endDate

        // Auto-generate name if not provided
        if (!name) {
          name = generatePeriodName(startDate, endDate)
        }
      } else {
        throw new Error("Antingen ange 'month' ELLER både 'startDate' och 'endDate'")
      }

      // Check if period already exists
      const existing = await this.getPeriod(user, periodId)
      if (existing) {
        throw new Error('Perioden finns redan')
      }

      const docRef = this.getPeriodDocument(user, periodId)
      const now = serverTimestamp()

      const periodData = {
        id: periodId,
        status: 'draft' as PeriodStatus,
        startDate: Timestamp.fromDate(startDate),
        endDate: Timestamp.fromDate(endDate),
        ...(data.payoutDate && { payoutDate: Timestamp.fromDate(data.payoutDate) }),
        ...(name && { name }),
        createdAt: now,
        updatedAt: now,
      }

      await setDoc(docRef, periodData)

      const period = await this.getPeriod(user, periodId)
      if (!period) {
        throw new Error('Det gick inte att skapa perioden')
      }

      return period
    } catch (error) {
      console.error('Error creating period:', error)
      if (error instanceof Error) {
        throw error
      }
      throw new Error('Det gick inte att skapa perioden')
    }
  }

  // ============================================================================
  // Lock/Unlock Operations
  // ============================================================================

  /**
   * Check if a period is locked
   * Returns true if period is locked, false if draft or doesn't exist
   */
  static async isPeriodLocked(user: User, periodId: string): Promise<boolean> {
    const period = await this.getPeriod(user, periodId)
    return period?.status === 'locked'
  }

  /**
   * Lock a period
   * Creates the period document if it doesn't exist, or updates status if it does
   *
   * Note: This only sets the lock flag. The caller is responsible for creating
   * LockedSalaryData snapshots (typically done in the same transaction).
   */
  static async lockPeriod(user: User, periodId: string): Promise<Period> {
    try {
      this.validatePeriodFormat(periodId)

      // Check if period exists and is already locked
      const existing = await this.getPeriod(user, periodId)
      if (existing?.status === 'locked') {
        throw new Error('Perioden är redan låst')
      }

      const docRef = this.getPeriodDocument(user, periodId)
      const now = serverTimestamp()

      if (existing) {
        // Update existing period
        await updateDoc(docRef, {
          status: 'locked' as PeriodStatus,
          lockedAt: now,
          lockedBy: user.uid,
          updatedAt: now,
        })
      } else {
        // Create new period in locked state (edge case)
        const dates = getMonthlyPeriodDates(periodId)
        const name = generatePeriodName(dates.startDate, dates.endDate)

        await setDoc(docRef, {
          id: periodId,
          status: 'locked' as PeriodStatus,
          startDate: Timestamp.fromDate(dates.startDate),
          endDate: Timestamp.fromDate(dates.endDate),
          name,
          lockedAt: now,
          lockedBy: user.uid,
          createdAt: now,
          updatedAt: now,
        })
      }

      const updatedPeriod = await this.getPeriod(user, periodId)
      if (!updatedPeriod) {
        throw new Error('Det gick inte att låsa perioden')
      }

      return updatedPeriod
    } catch (error) {
      console.error('Error locking period:', error)
      if (error instanceof Error) {
        throw error
      }
      throw new Error('Det gick inte att låsa perioden')
    }
  }

  /**
   * Unlock a period with mandatory reason (for audit trail)
   * Adds unlock event to history array
   */
  static async unlockPeriod(
    user: User,
    periodId: string,
    reason: string
  ): Promise<Period> {
    try {
      this.validatePeriodFormat(periodId)

      if (!reason || reason.trim().length === 0) {
        throw new Error('Anledning för upplåsning är obligatorisk')
      }

      // Check if period exists and is locked
      const existing = await this.getPeriod(user, periodId)
      if (!existing) {
        throw new Error('Perioden finns inte')
      }
      if (existing.status !== 'locked') {
        throw new Error('Perioden är inte låst')
      }

      const docRef = this.getPeriodDocument(user, periodId)
      const now = Timestamp.now()

      // Create unlock history entry
      const unlockEntry: UnlockHistoryEntry = {
        unlockedAt: now,
        unlockedBy: user.uid,
        reason: reason.trim(),
      }

      // Update period: change status to draft and append to unlock history
      const currentHistory = existing.unlockHistory || []
      await updateDoc(docRef, {
        status: 'draft' as PeriodStatus,
        unlockHistory: [...currentHistory, unlockEntry],
        updatedAt: serverTimestamp(),
      })

      const updatedPeriod = await this.getPeriod(user, periodId)
      if (!updatedPeriod) {
        throw new Error('Det gick inte att låsa upp perioden')
      }

      return updatedPeriod
    } catch (error) {
      console.error('Error unlocking period:', error)
      if (error instanceof Error) {
        throw error
      }
      throw new Error('Det gick inte att låsa upp perioden')
    }
  }

  // ============================================================================
  // Wizard State Operations
  // ============================================================================

  /**
   * Update the wizard step for a period
   */
  static async updateWizardStep(
    user: User,
    periodId: string,
    step: WizardStep
  ): Promise<Period> {
    try {
      this.validatePeriodFormat(periodId)

      const existing = await this.getPeriod(user, periodId)
      if (!existing) {
        throw new Error('Perioden finns inte')
      }

      if (existing.status === 'locked') {
        throw new Error('Kan inte ändra wizard-steg för en låst period')
      }

      const docRef = this.getPeriodDocument(user, periodId)
      await updateDoc(docRef, {
        wizardStep: step,
        updatedAt: serverTimestamp(),
      })

      const updatedPeriod = await this.getPeriod(user, periodId)
      if (!updatedPeriod) {
        throw new Error('Det gick inte att uppdatera wizard-steget')
      }

      return updatedPeriod
    } catch (error) {
      console.error('Error updating wizard step:', error)
      if (error instanceof Error) {
        throw error
      }
      throw new Error('Det gick inte att uppdatera wizard-steget')
    }
  }

  /**
   * Update period metadata (name, payoutDate)
   */
  static async updatePeriod(
    user: User,
    periodId: string,
    data: UpdatePeriodData
  ): Promise<Period> {
    try {
      this.validatePeriodFormat(periodId)

      const existing = await this.getPeriod(user, periodId)
      if (!existing) {
        throw new Error('Perioden finns inte')
      }

      const docRef = this.getPeriodDocument(user, periodId)
      const updates: Record<string, unknown> = {
        updatedAt: serverTimestamp(),
      }

      if (data.name !== undefined) {
        updates.name = data.name
      }
      if (data.payoutDate !== undefined) {
        updates.payoutDate = Timestamp.fromDate(data.payoutDate)
      }
      if (data.wizardStep !== undefined) {
        if (existing.status === 'locked') {
          throw new Error('Kan inte ändra wizard-steg för en låst period')
        }
        updates.wizardStep = data.wizardStep
      }

      await updateDoc(docRef, updates)

      const updatedPeriod = await this.getPeriod(user, periodId)
      if (!updatedPeriod) {
        throw new Error('Det gick inte att uppdatera perioden')
      }

      return updatedPeriod
    } catch (error) {
      console.error('Error updating period:', error)
      if (error instanceof Error) {
        throw error
      }
      throw new Error('Det gick inte att uppdatera perioden')
    }
  }

  // ============================================================================
  // Driver Exclusion Operations
  // ============================================================================

  /**
   * Update the excluded driver IDs for a period
   * Used to exclude drivers from salary calculation when locking
   */
  static async updateExcludedDrivers(
    user: User,
    periodId: string,
    excludedDriverIds: string[]
  ): Promise<Period> {
    try {
      this.validatePeriodFormat(periodId)

      const existing = await this.getPeriod(user, periodId)
      if (!existing) {
        throw new Error('Perioden finns inte')
      }

      if (existing.status === 'locked') {
        throw new Error('Kan inte ändra exkluderade förare för en låst period')
      }

      const docRef = this.getPeriodDocument(user, periodId)
      await updateDoc(docRef, {
        excludedDriverIds,
        updatedAt: serverTimestamp(),
      })

      const updatedPeriod = await this.getPeriod(user, periodId)
      if (!updatedPeriod) {
        throw new Error('Det gick inte att uppdatera perioden')
      }

      return updatedPeriod
    } catch (error) {
      console.error('Error updating excluded drivers:', error)
      if (error instanceof Error) {
        throw error
      }
      throw new Error('Det gick inte att uppdatera exkluderade förare')
    }
  }
}
